'use client';

import { useEffect, useMemo, useState } from 'react';

import L, { LatLngTuple } from 'leaflet';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import { calculateMaxDistance, calculateTrajectoryAngle, moveAlongTrajectoryByDegrees } from './helpers';
import { TCoordinates, TLocationCoords } from '@/types';

import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';

const defaultIcon = L.icon({
  iconUrl: '/static/icons/map-marker.svg',
  shadowUrl: '/static/icons/map-marker.svg',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});
L.Marker.prototype.options.icon = defaultIcon;

export const startIcon = L.icon({
  iconUrl: '/static/icons/map-marker-start.svg',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export const runnerIcon = L.icon({
  iconUrl: '/static/icons/map-marker-runner.svg',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

export const endIcon = L.icon({
  iconUrl: '/static/icons/map-marker-end.svg',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export type Location = {
  lat: number;
  lon: number;
  city_name: string;
};

interface IMyMapProps {
  locations: Array<TLocationCoords>;
  zoom: number;
}

const getPosition = (location: TLocationCoords | TCoordinates) =>
  [Number(location.lon), Number(location.lat)] as LatLngTuple;

export default function MyMap({ locations = [], zoom }: IMyMapProps) {
  const [runnerPosition, setRunnerPosition] = useState(() => getPosition(locations[0]));

  const maxDistance = useMemo(() => {
    if (locations.length < 2) return 0;
    return calculateMaxDistance(locations) / 300;
  }, [locations]);
  const mapX = useMemo(() => {
    const sumCoordsum = locations.reduce((acc, { lon }) => acc + Number(lon), 0);
    const avgCoords = sumCoordsum / locations.length;
    return avgCoords;
  }, [locations]);
  const mapY = useMemo(() => {
    const sumCoords = locations.reduce((acc, { lat }) => acc + Number(lat), 0);
    const avgCoords = sumCoords / locations.length;
    return avgCoords;
  }, [locations]);

  const runnerCoords = useMemo(() => {
    if (locations.length < 2) return [{ lat: locations[0].lat, lon: locations[0].lon }];
    const results: Array<TCoordinates> = [];
    let [current, next] = locations as Array<TCoordinates>;
    current.lat = Number(current.lat);
    current.lon = Number(current.lon);

    let nextIndex = 1;
    results.push({ lat: current.lat, lon: current.lon });

    let angle = calculateTrajectoryAngle(current, next);
    let { lat, lon } = moveAlongTrajectoryByDegrees(current, angle, maxDistance);
    const newPosition = [lon, lat];

    while (nextIndex < locations.length) {
      const start = current;
      if (!locations[nextIndex]) break;

      current = moveAlongTrajectoryByDegrees(current, angle, maxDistance);

      const isDescending = start.lat < current.lat;
      const hasReachedNext = isDescending ? current.lat >= next.lat : next.lat >= current.lat;

      if (hasReachedNext) {
        current = next;
        nextIndex++;
        next = locations[nextIndex];
        if (next) angle = calculateTrajectoryAngle(current, next);
      }

      let { lat, lon } = current;
      results.push({ lat: Number(lat), lon: Number(lon) });
    }

    return results;
  }, [locations]);

  useEffect(() => {
    let currentIndex = 0;
    let timeoutId: NodeJS.Timeout | null = null;

    const updatePosition = () => {
      if (runnerCoords && runnerCoords.length > 0) {
        const coords = runnerCoords[currentIndex];
        const position = getPosition(coords);
        setRunnerPosition(position);

        currentIndex = (currentIndex + 1) % runnerCoords.length; // Increment index and wrap around

        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(updatePosition, 3);
      }
    };

    // Start the loop
    updatePosition();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [runnerCoords]);

  return (
    <MapContainer
      center={[mapX, mapY]}
      zoom={zoom}
      scrollWheelZoom={false}
      doubleClickZoom={false}
      style={{ height: '100%', width: '100%' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {/* start destination */}
      <Marker icon={startIcon} position={getPosition(locations[0])} zIndexOffset={900}>
        <Popup>{locations[0].city_name}</Popup>
      </Marker>

      {/* destinations */}
      {locations.length > 1 &&
        locations.map((location, index) => (
          <Marker key={`${location.city_name}${index}`} position={getPosition(location)} zIndexOffset={800}>
            <Popup>{location.city_name}</Popup>
          </Marker>
        ))}

      {/* runner */}
      {locations.length > 2 && <Marker icon={runnerIcon} position={runnerPosition} zIndexOffset={999} />}

      {/* end destination */}
      {locations.length > 1 && (
        <Marker icon={endIcon} position={getPosition(locations[locations.length - 1])} zIndexOffset={900}>
          <Popup>{locations[locations.length - 1].city_name}</Popup>
        </Marker>
      )}

      <Polyline positions={locations.map(getPosition)} stroke={true} color="#0A7BBD"></Polyline>
    </MapContainer>
  );
}
